'use client';
import cn from 'classnames';
import Image from 'next/image';
import NextLink from 'next/link';
import { useEffect, useState } from 'react';

import { useLoggedInUser } from '@/components/hook/user';
import { priveeLandingPath } from '@/util/user_client';
import { HeaderCMSContent } from '@/components/common/Header/Header';
import Text from '@/components/common/Text/Text';
import Link from '@/components/common/Link/Link';
import Button from '@/components/common/Button/Button';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';
import { useFlags } from '@/components/hook/flags';
import { SIGNUP_ROUTES } from '@/const/signup';
import { FLAG_KEYS } from '@/const/flags';
import { CACHE_TIME } from '@/const/cache';
import analytics from '@/util/analytics';
import { ReactivateAnalyticsMetadata } from '@/components/hook/analytics';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';
import { USER_STATUS } from '@/types/User';

import WordmarkBlack from 'public/img/logo/vivrelle-wordmark-black.svg';
import ArrowRight from 'public/img/icon/arrow-right.svg';
import Heart from 'public/img/icon/like-1.svg';
import CloseIcon from 'public/img/icon/close.svg';
import CollectionMenu from './CollectionMenu';
import './MobileMenu.style.scss';
import PriveeWordmarkBlack from 'public/img/logo/reserve-logo.svg';
import VivrelleMorganStewartLogo from 'public/img/logo/vivrelle-morgan-stewart-logo.png';

type MobileMenuProps = {
  className?: string;
  onCloseClick: () => void;
  cmsContent?: HeaderCMSContent;
};

function MobileMenu(props: MobileMenuProps) {
  const { className = '', onCloseClick, cmsContent } = props;
  const [showCollectionMenu, setShowCollectionMenu] = useState<boolean>(false);
  const { value: flagValues } = useFlags();

  const showCollabLink = flagValues[FLAG_KEYS.MORGAN_STEWART_NAVBAR];
  const morganStewartCollabLink = {
    href: '/collection/morganstewart',
    logo: VivrelleMorganStewartLogo,
    logoHeight: 28,
    alt: 'Vivrelle x Morgan Stewart wordmark'
  };

  const user = useLoggedInUser();

  const { isIOSWebView } = useContextBroker();

  // State for reactivation analytics data (fetched for cancelled users)
  const [reactivateAnalyticsMetadata, setReactivateAnalyticsMetadata] =
    useState<ReactivateAnalyticsMetadata>({
      membership_tier: '',
      add_ons: false,
      insurance_coverage: ''
    });

  // Fetch previous subscription data for cancelled users (for analytics)
  useEffect(() => {
    const fetchReactivationData = async () => {
      if (user?.status !== USER_STATUS.CANCELLED) return;

      try {
        const response = await fetch('/api/user/reactivate', {
          method: 'GET',
          headers: { 'content-type': 'application/json' },
          next: { revalidate: CACHE_TIME.FIVE_MINUTES }
        });

        if (response.ok) {
          const json = await response.json();
          if (json.success && json.data) {
            const subscriptionProduct = json.data
              .subscriptionProduct as SubscriptionProduct | null;
            const insuranceProduct = json.data
              .insuranceProduct as InsuranceProduct | null;

            setReactivateAnalyticsMetadata({
              membership_tier: subscriptionProduct?.name ?? '',
              add_ons: false, // Cancelled users don't have active add-ons
              insurance_coverage: insuranceProduct?.name ?? ''
            });
          }
        }
      } catch (error) {
        console.error('Error fetching reactivation data for analytics:', error);
      }
    };

    fetchReactivationData();
  }, [user?.status]);

  const handleCollectionClick = () => {
    setShowCollectionMenu(true);
  };

  const onCollectionCloseClick = () => {
    setShowCollectionMenu(false);
  };

  // Check if user has payment issues
  const userHasPaymentIssues =
    user?.isPastDue === true ||
    user?.status === USER_STATUS.PAST_DUE ||
    user?.status === USER_STATUS.PAYMENT_NEEDED;

  // Check if cancelled user can self-reactivate
  const canSelfReactivate =
    user?.status === USER_STATUS.CANCELLED &&
    !user?.isLocked &&
    !user?.isFraud &&
    !userHasPaymentIssues;

  return (
    <div className={cn('MobileMenu', className)}>
      <div className='MobileMenu-logoBar'>
        <Image
          src={WordmarkBlack}
          height={20}
          width={123}
          alt='Vivrelle wordmark in black'
        />
        <Image
          className='MobileMenu-closeIcon'
          src={CloseIcon}
          height={22}
          width={22}
          alt='Close Mobile Menu'
          onClick={onCloseClick}
        />
      </div>
      <div className='MobileMenu-links-container'>
        <div className='MobileMenu-main-links'>
          <button
            className='link link-with-arrow cursor'
            onClick={handleCollectionClick}
          >
            <Text className='collection-menu uppercase'>THE COLLECTION</Text>
            <Image
              className='arrow'
              src={ArrowRight}
              height={15}
              width={15}
              alt='View the collection'
            />
          </button>
          <Link
            className='link'
            href='/how-it-works'
          >
            HOW IT WORKS
          </Link>
          {showCollabLink && (
            <a
              className='link'
              href={morganStewartCollabLink.href}
            >
              <Image
                className='Header-vivrelle-logo'
                src={morganStewartCollabLink.logo}
                height={morganStewartCollabLink.logoHeight}
                alt={morganStewartCollabLink.alt}
              />
            </a>
          )}

          <a
            className='link'
            href={priveeLandingPath(user)}
          >
            <Image
              className='Privee-wordmark-svg'
              src={PriveeWordmarkBlack}
              height={28}
              alt='Réservé wordmark in black'
            />
          </a>

          {user ? (
            <>
              <Link
                className='link-with-icon'
                href='/collection?filters=my-wishlist'
              >
                <Image
                  className='link-icon'
                  src={Heart}
                  height={25}
                  width={25}
                  alt='View your wishlist'
                />
                <Text className='link-text'>WISHLIST</Text>
              </Link>

              {user?.status === USER_STATUS.FROZEN && (
                <a
                  className='Button dark MobileMenu-unfreeze-cta'
                  href={
                    isIOSWebView ? '/myaccount/unfreeze' : '/account/unfreeze'
                  }
                >
                  Unfreeze
                </a>
              )}

              {canSelfReactivate && (
                <a
                  className='Button pink-400 MobileMenu-reactivate-cta'
                  href='/account/reactivate'
                  onClick={() => {
                    analytics.track(
                      'Reactivate Navigation CTA - Clicked',
                      reactivateAnalyticsMetadata
                    );
                  }}
                >
                  Reactivate
                </a>
              )}
            </>
          ) : (
            <Link
              className='application-link'
              href={SIGNUP_ROUTES.ACCOUNT}
            >
              <Button
                className='application-link-Button'
                variant='light'
                onClick={() => {
                  if (window.location.pathname === '/reserve') {
                    sessionStorage.setItem(
                      'applying_from_privee_landing_page',
                      'true'
                    );
                  }
                }}
              >
                APPLY NOW
              </Button>
            </Link>
          )}
        </div>

        <div className='MobileMenu-headerCards'>
          {cmsContent?.mobileHeaderCard1 && (
            <div className='MobileMenu-headerCard headerCard-1'>
              {cmsContent.mobileHeaderCard1.url ? (
                <a href={cmsContent.mobileHeaderCard1.url}>
                  <Image
                    className='headerCard-image headerCard-image-1'
                    src={cmsContent.mobileHeaderCard1.image}
                    height={cmsContent.mobileHeaderCard1.imageHeight}
                    width={cmsContent.mobileHeaderCard1.imageWidth}
                    alt={cmsContent.mobileHeaderCard1.imageAltText}
                  />
                </a>
              ) : (
                <Image
                  className='headerCard-image headerCard-image-1'
                  src={cmsContent.mobileHeaderCard1.image}
                  height={cmsContent.mobileHeaderCard1.imageHeight}
                  width={cmsContent.mobileHeaderCard1.imageWidth}
                  alt={cmsContent.mobileHeaderCard1.imageAltText}
                />
              )}
            </div>
          )}
          {cmsContent?.mobileHeaderCard2 && (
            <div className='MobileMenu-headerCard headerCard-2'>
              {cmsContent.mobileHeaderCard2.url ? (
                <NextLink href={cmsContent.mobileHeaderCard2.url}>
                  <Image
                    className='headerCard-image headerCard-image-2'
                    src={cmsContent.mobileHeaderCard2.image}
                    height={cmsContent.mobileHeaderCard2.imageHeight}
                    width={cmsContent.mobileHeaderCard2.imageWidth}
                    alt={cmsContent.mobileHeaderCard2.imageAltText}
                  />
                </NextLink>
              ) : (
                <Image
                  className='headerCard-image headerCard-image-2'
                  src={cmsContent.mobileHeaderCard2.image}
                  height={cmsContent.mobileHeaderCard2.imageHeight}
                  width={cmsContent.mobileHeaderCard2.imageWidth}
                  alt={cmsContent.mobileHeaderCard2.imageAltText}
                />
              )}
            </div>
          )}
        </div>

        <CollectionMenu
          className={cn('MobileMenu-CollectionMenu', {
            show: showCollectionMenu
          })}
          showContent={showCollectionMenu}
          onCloseClick={onCollectionCloseClick}
          featuredLinks={cmsContent?.featuredLinks}
        />
      </div>
    </div>
  );
}

export default MobileMenu;
